'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingWidget({ isOpen, onClose }: BookingWidgetProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '1',
    apartmentType: 'luxos'
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.open('https://bookingengine.stayflexi.com/?hotel_id=33526', '_blank');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isHydrated || !isOpen) return null;

  return (
    <div className="fixed inset-0 bg-primary/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
          <h3 className="text-2xl font-display font-bold text-primary">
            Check Availability
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors duration-300"
            aria-label="Close booking widget"
          >
            <Icon name="XMarkIcon" size={24} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="checkIn" className="block text-sm font-semibold text-primary">
                Check-in Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="checkIn"
                  name="checkIn"
                  value={formData.checkIn}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="checkOut" className="block text-sm font-semibold text-primary">
                Check-out Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="checkOut"
                  name="checkOut"
                  value={formData.checkOut}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="apartmentType" className="block text-sm font-semibold text-primary">
              Apartment Type
            </label>
            <select
              id="apartmentType"
              name="apartmentType"
              value={formData.apartmentType}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="luxos">Luxos Luxury (From ₹4,750/night)</option>
              <option value="primera">Primera Premium (From ₹3,850/night)</option>
              <option value="classica">Classica Comfort (From ₹3,100/night)</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="guests" className="block text-sm font-semibold text-primary">
              Number of Guests
            </label>
            <select
              id="guests"
              name="guests"
              value={formData.guests}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value="1">1 Guest</option>
              <option value="2">2 Guests</option>
              <option value="3">3 Guests</option>
              <option value="4">4 Guests</option>
            </select>
          </div>

          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="CheckCircleIcon" size={16} className="text-success" />
              <span>Free cancellation up to 48 hours before check-in</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="CheckCircleIcon" size={16} className="text-success" />
              <span>Best price guarantee</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="CheckCircleIcon" size={16} className="text-success" />
              <span>Instant confirmation</span>
            </div>
          </div>

          <button
            type="submit"
            className="w-full px-8 py-4 bg-accent hover:bg-accent/90 text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
          >
            <span>Continue to Booking</span>
            <Icon name="ArrowRightIcon" size={20} className="text-white" />
          </button>
        </form>
      </div>
    </div>
  );
}